// useArtists.js
// Shared hook for the artists/{id} registry — the ONE canonical identity list
// for everyone who draws pancakes. Both the shift-open roster picker and the
// Practice Studio draw from this; order.artistName stays the display string
// (17+ consumers) while order.artistId carries the stable identity.
//
// Doc shape: { name, emoji?, email? (null = no login; set = Practice Studio
// login via the staffRoles/{email} pointer), aliases?: [], active, addedAt }.
// Doc id = slug of the name at creation; renames keep the id stable.

import { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";

export function artistSlug(name) {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || `artist_${Date.now()}`;
}

// All lowercase name keys that should attribute to this artist — canonical
// name + aliases. Used to match free-text artistName strings in historical
// shift summaries so past pancakes tether to the registry identity.
export function artistNameKeys(artist) {
  const keys = [artist.name, ...(artist.aliases || [])]
    .filter(Boolean)
    .map((n) => n.trim().toLowerCase());
  return [...new Set(keys)];
}

export default function useArtists() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onSnapshot(collection(db, "artists"), (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (a.addedAt || 0) - (b.addedAt || 0));
      setAll(rows);
      setLoading(false);
    }, () => setLoading(false));
  }, []);

  const artists = all.filter((a) => a.active !== false);
  const artistMap = Object.fromEntries(all.map((a) => [a.id, a]));
  const byName = {};
  for (const a of all) {
    for (const key of artistNameKeys(a)) byName[key] = a;
  }

  return { artists, allArtists: all, artistMap, byName, loading };
}
